// pages_json contract — API §3.6 (C5 compiler) and App Flow §4 (reader).
import { z } from 'zod';
import { isoDate } from './entities.js';
import { departmentType } from './enums.js';

export const layoutHint = z.enum([
  'text_left_photo_right',
  'text_right_photo_left',
  'text_centered',
  'photo_full_bleed',
  'text_over_photo',
]);
export type LayoutHint = z.infer<typeof layoutHint>;

export const editionPhotoSchema = z.object({
  photo_id: z.string(),
  storage_key: z.string(),
  alt_text: z.string(),
  caption: z.string().nullable().optional(),
});

export const coverPageSchema = z.object({
  index: z.number().int(),
  type: z.literal('cover'),
  theme: z.string(),
  month_label: z.string(),
  year_month: z.string(),
  family_name: z.string(),
  editor_name: z.string(),
  member_count: z.number().int(),
});

export const storyPageSchema = z.object({
  index: z.number().int(),
  type: z.literal('story'),
  story_id: z.string().nullable(),
  contribution_id: z.string().nullable(),
  user_id: z.string().nullable(),
  member_name: z.string(),
  prompt_text: z.string(),
  narrative: z.string(),
  layout_hint: layoutHint,
  photos: z.array(editionPhotoSchema),
});

/** Deterministic crossword + word search — API §3.5 C4.1, architecture §3.6. */
export const puzzleContentSchema = z.object({
  crossword: z.object({
    size: z.number().int(),
    grid: z.array(z.array(z.string())), // '' = block, letter otherwise
    entries: z.array(
      z.object({
        number: z.number().int(),
        direction: z.enum(['across', 'down']),
        row: z.number().int(),
        col: z.number().int(),
        answer: z.string(),
        clue: z.string(),
      }),
    ),
  }),
  word_search: z.object({
    size: z.number().int(),
    grid: z.array(z.array(z.string())),
    words: z.array(
      z.object({
        word: z.string(),
        row: z.number().int(),
        col: z.number().int(),
        direction: z.enum(['E', 'S', 'SE', 'NE']),
      }),
    ),
  }),
});
export type PuzzleContent = z.infer<typeof puzzleContentSchema>;

export const horoscopeContentSchema = z.object({
  horoscopes: z.array(z.object({ member_name: z.string(), zodiac: z.string(), text: z.string() })),
});

export const foodContentSchema = z.object({
  recipe: z.object({
    title: z.string(),
    description: z.string(),
    prep_time: z.string(),
    ingredients: z.array(z.string()),
    steps: z.array(z.string()),
    dietary_note: z.string(),
  }),
});

export const travelContentSchema = z.object({
  destination: z.object({
    name: z.string(),
    country: z.string(),
    why_your_family_would_love_it: z.string(),
    fun_fact: z.string(),
  }),
});

export const photoCollectionContentSchema = z.object({
  collection_title: z.string(),
  selected_photos: z.array(
    z.object({
      photo_id: z.string(),
      storage_key: z.string().optional(),
      caption: z.string(),
      alt_text: z.string().optional(),
      display_order: z.number().int(),
    }),
  ),
});

export const occasionsContentSchema = z.object({
  cards: z.array(
    z.object({
      recipient_name: z.string(),
      occasion_type: z.string(),
      date: isoDate.optional(),
      message: z.string(),
      design_hint: z.string(),
    }),
  ),
});

export const departmentPageSchema = z.object({
  index: z.number().int(),
  type: z.literal('department'),
  department_type: departmentType,
  content: z.unknown(),
});

/** API §3.6 — failed or skipped departments become a friendly placeholder page. */
export const placeholderPageSchema = z.object({
  index: z.number().int(),
  type: z.literal('placeholder'),
  department_type: departmentType,
  message: z.string(),
  design_hint: z.literal('friendly_absent'),
});

export const creditsPageSchema = z.object({
  index: z.number().int(),
  type: z.literal('credits'),
  editor_name: z.string(),
  contributors: z.array(z.string()),
  made_with: z.literal('Roots'),
});

export const editionPageSchema = z.discriminatedUnion('type', [
  coverPageSchema,
  storyPageSchema,
  departmentPageSchema,
  placeholderPageSchema,
  creditsPageSchema,
]);
export type EditionPage = z.infer<typeof editionPageSchema>;

export const pagesJsonSchema = z.array(editionPageSchema);
export type PagesJson = z.infer<typeof pagesJsonSchema>;

export const PLACEHOLDER_MESSAGE =
  "This department is taking a break this month. It'll be back next edition!";
